import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { AtlasRecord, CreateRecordInput, FieldDefinition, MapDefinition } from '@grs-atlas/shared';
import { US_STATE_CODES, US_STATE_NAMES } from '@grs-atlas/shared';

interface RecordFormProps {
  map: MapDefinition;
  initial?: AtlasRecord;
  defaultState?: string;
  onSubmit: (data: CreateRecordInput) => Promise<void> | void;
  onCancel: () => void;
  submitting?: boolean;
  error?: string;
}

type FormValues = {
  name: string;
  stateCodes: string[];
  tags: string;
  fields: Record<string, string | boolean | number | string[]>;
};

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (field.type === 'textarea') {
    return (
      <textarea
        rows={3}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        readOnly={field.readOnly}
      />
    );
  }
  if (field.type === 'boolean') {
    return (
      <select
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? undefined : e.target.value === 'true')
        }
        required={field.required}
        disabled={field.readOnly}
      >
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (field.type === 'select' || field.type === 'status' || field.type === 'confidence') {
    return (
      <select
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value || undefined)}
        required={field.required}
        disabled={field.readOnly}
      >
        <option value="">—</option>
        {(field.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === 'number' || field.type === 'currency' || field.type === 'percentage') {
    return (
      <input
        type="number"
        value={value === undefined || value === null ? '' : Number(value)}
        onChange={(e) =>
          onChange(e.target.value === '' ? undefined : Number(e.target.value))
        }
        required={field.required}
        readOnly={field.readOnly}
        min={field.min}
        max={field.max}
      />
    );
  }
  if (field.type === 'date') {
    return (
      <input
        type="date"
        value={String(value ?? '').slice(0, 10)}
        onChange={(e) => onChange(e.target.value || undefined)}
        required={field.required}
        readOnly={field.readOnly}
      />
    );
  }
  if (field.type === 'states') {
    return null; // handled top-level
  }
  const inputType =
    field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text';
  return (
    <input
      type={inputType}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      readOnly={field.readOnly}
      placeholder={field.placeholder}
    />
  );
}

export function RecordForm({
  map,
  initial,
  defaultState,
  onSubmit,
  onCancel,
  submitting,
  error,
}: RecordFormProps) {
  const { register, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    defaultValues: {
      name: initial?.name ?? '',
      stateCodes: initial?.stateCodes ?? (defaultState ? [defaultState] : []),
      tags: (initial?.tags ?? []).join(', '),
      fields: (initial?.fields as FormValues['fields']) ?? {},
    },
  });

  useEffect(() => {
    reset({
      name: initial?.name ?? '',
      stateCodes: initial?.stateCodes ?? (defaultState ? [defaultState] : []),
      tags: (initial?.tags ?? []).join(', '),
      fields: (initial?.fields as FormValues['fields']) ?? {},
    });
  }, [initial, defaultState, reset]);

  const stateCodes = watch('stateCodes') ?? [];

  const submit = handleSubmit(async (values) => {
    const knownKeys = new Set(map.fields.map((f) => f.key));
    const fields: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values.fields ?? {})) {
      if (knownKeys.has(key) && key !== 'name' && key !== 'stateCodes') {
        fields[key] = value;
      }
    }
    await onSubmit({
      name: values.name,
      stateCodes: values.stateCodes as CreateRecordInput['stateCodes'],
      tags: values.tags
        .split(/[,;]/)
        .map((t) => t.trim())
        .filter(Boolean),
      fields,
      status: 'active',
    });
  });

  const editableFields = map.fields.filter(
    (f) => f.key !== 'name' && f.key !== 'stateCodes',
  );

  return (
    <form className="form-grid" onSubmit={submit}>
      {error && (
        <div role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      )}
      <label>
        Name *
        <input {...register('name', { required: true })} />
      </label>
      <label>
        States *
        <select
          multiple
          size={8}
          value={stateCodes}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
            setValue('stateCodes', selected);
          }}
          required
        >
          {US_STATE_CODES.map((code) => (
            <option key={code} value={code}>
              {US_STATE_NAMES[code]} ({code})
            </option>
          ))}
        </select>
      </label>
      <label>
        Tags
        <input {...register('tags')} placeholder="comma-separated" />
      </label>
      {editableFields.map((field) => (
        <label key={field.key}>
          {field.label}
          {field.required ? ' *' : ''}
          {field.helpText && (
            <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{field.helpText}</span>
          )}
          <FieldInput
            field={field}
            value={watch(`fields.${field.key}`)}
            onChange={(v) => setValue(`fields.${field.key}`, v as never)}
          />
        </label>
      ))}
      <div className="form-actions">
        <button type="button" className="btn secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn" disabled={submitting}>
          {submitting ? 'Saving…' : initial ? 'Save Changes' : 'Create Record'}
        </button>
      </div>
    </form>
  );
}
